import { useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import {
  BookOpen,
  Upload,
  Loader2,
  CheckCircle2,
  DatabaseZap,
  Sparkles,
  RotateCcw,
  AlertTriangle,
  FileText,
  Clock,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Spinner,
  Textarea,
  useToast,
} from '../../ui';
import { MarkdownView } from '../Knowledge/MarkdownView';
import { CitationsPanel } from '../Chat/CitationsPanel';
import { GoalKnowledgeBaseStatus } from './GoalKnowledgeBaseStatus';
import { useGoalKnowledge } from '../../features/goals/useGoalKnowledge';
import { formatDate } from '../../features/goals/goalModel';
import type { Goal } from '../../features/goals/types';

/** Example questions seeded into the Ask box for an empty goal. */
const EXAMPLES = [
  'What do the uploaded docs say the plan is for this goal?',
  'What are the key risks and dependencies for this goal?',
  'Summarize the scope and success metrics for this goal.',
];

/**
 * Goal → Documents & Knowledge Base.
 *
 * Three flows for one goal, all against the SAME team knowledge base:
 *  1. Upload documents to the goal's scoped prefix in the KB's S3 data source.
 *  2. Build / Update the knowledge base (StartIngestion) so they are indexed.
 *  3. Ask Vector about this goal (RetrieveAndGenerate) — a grounded answer with
 *     citations, rendered with the shared MarkdownView + CitationsPanel.
 *
 * Reuses the KB API + MarkdownView/CitationsPanel + the store/hook
 * layering; presentational logic only — all data access flows through
 * {@link useGoalKnowledge}.
 */
export function GoalKnowledgePanel({ goal }: { goal: Goal }) {
  const { toast } = useToast();
  const {
    documents,
    documentsLoading,
    ingestedCount,
    hasIngestedDocuments,
    uploading,
    uploadDoc,
    building,
    buildStatus,
    buildKnowledgeBase,
    asking,
    answer,
    lastQuestion,
    askError,
    ask,
    clearAnswer,
  } = useGoalKnowledge(goal.id, goal);

  const fileRef = useRef<HTMLInputElement>(null);
  const [question, setQuestion] = useState('');

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    try {
      const content = await file.text();
      await uploadDoc(file.name, content);
      toast({
        tone: 'success',
        title: 'Document uploaded',
        description: `${file.name} — build the knowledge base to index it.`,
      });
    } catch (err) {
      toast({
        tone: 'error',
        title: 'Upload failed',
        description:
          err instanceof Error ? err.message : 'Could not upload document.',
      });
    }
  };

  const onBuild = async () => {
    try {
      await buildKnowledgeBase();
      toast({
        tone: 'success',
        title: 'Knowledge base update started',
        description: 'Ingestion is running — docs become answerable shortly.',
      });
    } catch (err) {
      toast({
        tone: 'error',
        title: 'Build failed',
        description:
          err instanceof Error ? err.message : 'Could not start ingestion.',
      });
    }
  };

  const submitQuestion = () => {
    const q = question.trim();
    if (!q || asking) return;
    void ask(q);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitQuestion();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-accent" aria-hidden="true" />
            Documents & Knowledge Base
          </span>
        </CardTitle>
        <span className="flex items-center gap-2">
          {hasIngestedDocuments && (
            <Badge tone="success" dot>
              {ingestedCount} indexed
            </Badge>
          )}
        </span>
      </CardHeader>
      <CardBody className="space-y-5">
        <p className="text-sm text-zinc-400">
          Upload documents for this goal, build them into the team knowledge
          base, then ask Vector questions grounded in them.
        </p>

        {/* 0 — Goal knowledge base provisioning status */}
        <GoalKnowledgeBaseStatus />

        {/* 1 — Upload */}
        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            1 · Upload documents
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".txt,.md,.json,.csv,.log,text/*"
            className="hidden"
            onChange={(e) => void onFile(e)}
          />
          <Button
            variant="outline"
            size="sm"
            loading={uploading}
            leftIcon={!uploading ? <Upload className="h-4 w-4" /> : undefined}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Choose a text file'}
          </Button>
          <p className="text-xs text-zinc-600">
            Stored under a goal-scoped prefix in the KB’s S3 data source.
          </p>

          {documentsLoading && documents.length === 0 && (
            <p className="flex items-center gap-2 pt-1 text-xs text-zinc-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading documents…
            </p>
          )}

          {documents.length > 0 && (
            <ul className="space-y-1.5 pt-1">
              {documents.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center gap-2 rounded-lg border border-surface-border bg-surface-1/60 px-3 py-1.5 text-xs text-zinc-300"
                >
                  <FileText
                    className="h-3.5 w-3.5 shrink-0 text-accent"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 truncate" title={d.kbObjectKey}>
                    {d.fileName}
                    {d.addedBy && (
                      <span className="text-zinc-600">
                        {' '}
                        · {d.addedBy}
                        {d.addedAt ? ` · ${formatDate(d.addedAt)}` : ''}
                      </span>
                    )}
                  </span>
                  {d.ingested ? (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-400">
                      <CheckCircle2 className="h-3 w-3" />
                      Indexed
                    </span>
                  ) : (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded bg-surface-3 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-500">
                      <Clock className="h-3 w-3" />
                      Pending build
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 2 — Build / Update */}
        <div className="space-y-2 border-t border-surface-border pt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            2 · Build / update the knowledge base
          </p>
          <Button
            variant="secondary"
            size="sm"
            loading={building}
            leftIcon={
              !building ? <DatabaseZap className="h-4 w-4" /> : undefined
            }
            onClick={() => void onBuild()}
          >
            {building ? 'Starting ingestion…' : 'Build / Update Knowledge Base'}
          </Button>
          {buildStatus && (
            <p className="flex items-start gap-1.5 text-xs text-zinc-400">
              {building ? (
                <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
              )}
              <span className="break-all">{buildStatus}</span>
            </p>
          )}
        </div>

        {/* 3 — Ask Vector about this goal */}
        <div className="space-y-2 border-t border-surface-border pt-4">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
              3 · Ask Vector about this goal
            </p>
            {answer && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAnswer}
                leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
              >
                Clear
              </Button>
            )}
          </div>

          {!answer && !asking && (
            <div className="flex flex-wrap gap-1.5 pb-1">
              {EXAMPLES.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => void ask(q)}
                  className="rounded-lg border border-surface-border bg-surface-1 px-2.5 py-1 text-xs text-zinc-400 transition-colors hover:border-accent/40 hover:text-zinc-200"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Textarea
                rows={1}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Ask a question grounded in this goal’s documents…"
                className="max-h-40 min-h-[2.5rem] resize-none"
                aria-label="Ask Vector about this goal"
                disabled={asking}
              />
            </div>
            <Button
              onClick={submitQuestion}
              loading={asking}
              disabled={!question.trim()}
              leftIcon={!asking ? <Sparkles className="h-4 w-4" /> : undefined}
            >
              Ask
            </Button>
          </div>

          {asking && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Spinner label="Retrieving and answering" />
              <span>Retrieving from the knowledge base…</span>
            </div>
          )}

          {askError && !asking && (
            <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">Grounded answer unavailable</p>
                <p className="mt-0.5 text-xs text-red-300/80">{askError}</p>
              </div>
            </div>
          )}

          {answer && !asking && (
            <div className="space-y-2">
              {lastQuestion && (
                <p className="text-xs text-zinc-500">
                  <span className="font-medium text-zinc-400">Asked:</span>{' '}
                  {lastQuestion}
                </p>
              )}
              <div className="rounded-xl border border-surface-border bg-surface-1/60 px-3.5 py-3">
                {answer.answer.trim() ? (
                  <MarkdownView markdown={answer.answer} />
                ) : (
                  <p className="text-sm text-zinc-500">
                    The knowledge base returned an empty answer.
                  </p>
                )}
              </div>
              {answer.citations.length > 0 && (
                <CitationsPanel citations={answer.citations} />
              )}
            </div>
          )}
        </div>

        {/* Shared-KB note */}
        <p className="rounded-xl border border-surface-border bg-surface-1/40 px-3 py-2.5 text-xs leading-relaxed text-zinc-500">
          Documents you build here are indexed into the team knowledge base, so
          they’re then usable by <span className="text-zinc-300">Ask Vector</span>,
          task <span className="text-zinc-300">KB Research</span>, and{' '}
          <span className="text-zinc-300">Estimate with Vector</span> — the same
          knowledge base powers them all.
        </p>
      </CardBody>
    </Card>
  );
}

export default GoalKnowledgePanel;
